import { useFlags } from 'launchdarkly-react-client-sdk';
import isNil from 'lodash/isNil';
import { useState, useMemo, useEffect } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { toastr } from 'react-redux-toastr';
import { getTextingPhoneNumber } from '../../../utils/property-helpers';
import PropertyService from '../../../services/propertyService';
import useCustomEvent from '../../../hooks/useCustomEvent';
import { useSendMessage } from '../../../hooks/useSendMessage';
import { CONVERSATION_STATUS_CHANGED_EVENT } from '../../TextingTab/customEvents';
import intlMessages from '../messages';
import {
  fetchMessages,
  fetchCommunicationPreferencesByCustomer,
  fetchCommunicationPreferencesByProspect,
  sendMessage as sendMessageApi,
  updateConversationStatus,
} from './data';
import { messageDtoToMessage } from './mappings';
import { effectiveConversationStatus } from '../../../utils/texting';
import { useAppFlags } from '../../../utils/featureFlags';
import { resolveMessageNeedsHelp } from 'containers/TextingTab/data';
import type { Conversation } from '../types';

type UseChatContainerParams = {
  selectedConversation: ?Conversation,
  onStatusChange: () => void,
  updateConversationLocally: (conversationId: string, updates: Object) => void,
  intl: any,
};

export function useChatContainer({
  selectedConversation,
  onStatusChange,
  updateConversationLocally,
  intl,
}: UseChatContainerParams): any {
  const [message, setMessage] = useState('');
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const {
    twoWayTextingDisplayFortressUsername,
    twoWayTextingAcceptImages,
    textingCharacterLimitIncrease,
    smsDoubleOptIn,
  } = useFlags();
  const { leslie: leslieEnabled } = useAppFlags();

  const { emit: emitConversationStatusChanged } = useCustomEvent({
    eventKey: CONVERSATION_STATUS_CHANGED_EVENT,
  });

  // Fetch the property data for the conversation's property to get the texting phone number
  const propertyService = useMemo(() => new PropertyService(), []);

  const organizationId =
    selectedConversation?.recipient?.property?.organizationId;
  const propertyId = selectedConversation?.propertyId;
  const conversationId = selectedConversation?.conversationId;
  const customerId = selectedConversation?.recipient?.customerId;
  const prospectId = selectedConversation?.recipient?.prospectId;

  const queryEnabled = !isNil(propertyId) && !isNil(organizationId);

  const { data: conversationProperty, isLoading: isPropertyLoading } = useQuery(
    ['property', propertyId],
    async () => {
      const property = await propertyService.getOneProperty(
        organizationId || '',
        propertyId || '',
      );
      return property;
    },
    {
      enabled: queryEnabled,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    },
  );

  useEffect(() => {
    setReplyingToMessage(null);
  }, [selectedConversation?.conversationId]);

  // Fetch communication preferences directly by customerId or prospectId
  const {
    data: communicationPreferences,
    isLoading: isPreferencesLoading,
    refetch: refreshCommunicationPreferences,
  } = useQuery(
    ['communicationPreferences', customerId, prospectId],
    () => {
      if (customerId) {
        return fetchCommunicationPreferencesByCustomer({ customerId });
      }
      if (prospectId) {
        return fetchCommunicationPreferencesByProspect({ prospectId });
      }
      return null;
    },
    {
      enabled: !!(customerId || prospectId),
      onError: (error) => {
        toastr.error(
          intl.formatMessage(intlMessages.errorHeader),
          intl.formatMessage(
            intlMessages.failedToLoadCommunicationPreferencesErrorMsg,
          ),
        );
      },
    },
  );

  const infoOptedIn = useMemo(() => {
    if (!communicationPreferences) return false;

    const infoPreference = communicationPreferences.find(
      (preference) => preference.communicationChannel.type === 'info',
    );
    return infoPreference?.isOptedIn || false;
  }, [communicationPreferences]);

  const textingPhoneNumber = useMemo(() => {
    const phoneNumber = getTextingPhoneNumber(conversationProperty);
    return phoneNumber;
  }, [conversationProperty]);

  const {
    data,
    isFetching,
    isFetchingPreviousPage,
    fetchPreviousPage: loadMoreMessages,
    refetch: refreshMessages,
    hasPreviousPage,
  } = useInfiniteQuery({
    enabled: !isNil(conversationId),
    initialData: { pages: [] },
    // The flags read below stay out of the key: none of them changes the request, only how the
    // response is mapped, so keying on them would split the cache for no gain.
    queryKey: ['chatMessages', conversationId],
    queryFn: async ({ pageParam }) => {
      if (!conversationId || !selectedConversation) return [];

      const messages = await fetchMessages({
        conversationId,
        cursor: pageParam,
        includeUser: twoWayTextingDisplayFortressUsername,
        includeAttachments: twoWayTextingAcceptImages,
      });

      return messages.map((m) =>
        messageDtoToMessage({
          messageDto: m,
          conversation: selectedConversation,
          user: m.user,
          includeUser: twoWayTextingDisplayFortressUsername,
          includeAttachments: twoWayTextingAcceptImages,
          leslieEnabled,
        }),
      );
    },
    getPreviousPageParam: (oldestPage, pages) => {
      const oldestMessage = oldestPage?.[0];
      return oldestMessage ? `before__${oldestMessage.id}` : null;
    },
    // refetchInterval: 60000, // Auto-refresh disabled
    onError: () => {
      toastr.error(
        intl.formatMessage(intlMessages.errorHeader),
        intl.formatMessage(intlMessages.failedToLoadMessagesError),
      );
    },
  });

  const messages = useMemo(() => data.pages.flat(), [data.pages]);

  function handleLoadMoreMessages() {
    loadMoreMessages();
  }

  function handleMessageChange(value: string) {
    setMessage(value);
  }

  const { isSending, handleSendMessage } = useSendMessage({
    refreshCommunicationPreferences,
    intl,
    messages: {
      errorHeader: intlMessages.errorHeader,
      userNoLongerOptedInErrorMsg: intlMessages.userNoLongerOptedInErrorMsg,
      failedToSendMessageError: intlMessages.failedToSendMessageError,
    },
    onSend: async (messageText: string) => {
      if (!conversationId || !selectedConversation) {
        throw new Error('No conversation selected');
      }

      const recipient = {};
      if (selectedConversation.recipient.prospectId) {
        recipient.prospectId = selectedConversation.recipient.prospectId;
      }
      if (selectedConversation.recipient.customerId) {
        recipient.customerId = selectedConversation.recipient.customerId;
      }

      await sendMessageApi({
        message: messageText,
        conversationId,
        recipient,
      });

      if (replyingToMessage) {
        try {
          await resolveMessageNeedsHelp({ messageId: replyingToMessage.id });
          setReplyingToMessage(null);
        } catch (error) {
          toastr.error(
            intl.formatMessage(intlMessages.errorHeader),
            intl.formatMessage(intlMessages.failedToResolveLeslieHelpErrorMsg),
          );
        }
      }

      await refreshMessages();
      onStatusChange();
      setMessage('');
    },
  });

  async function handleToggleResolved() {
    if (!conversationId || !selectedConversation) return;

    // Read through the needs-help flag: while it is open, conversationStatus is NEEDS_HELP and
    // the status this toggle actually controls lives in activeFlowStatus. Comparing the masked
    // value instead would peg the toggle to one direction and make it appear to do nothing.
    const isResolved =
      effectiveConversationStatus(selectedConversation) === 'RESOLVED';
    const conversationStatus = isResolved ? 'UNRESOLVED' : 'RESOLVED';

    try {
      const updated = await updateConversationStatus({
        conversationId,
        conversationStatus,
        propertyId: propertyId || '',
      });

      // Cache what the server settled on, not what we asked for, and carry activeFlowStatus with
      // it: a flagged conversation keeps NEEDS_HELP and records the new status underneath, so
      // dropping the shadow here would leave the banner showing the pre-toggle state.
      updateConversationLocally(conversationId, {
        conversationStatus: updated?.conversationStatus ?? conversationStatus,
        activeFlowStatus: updated?.activeFlowStatus,
      });
      // Emit event to refresh notification badges
      emitConversationStatusChanged();
    } catch (error) {
      toastr.error(
        intl.formatMessage(intlMessages.errorHeader),
        intl.formatMessage(intlMessages.failedToUpdateStatusError),
      );
    }
  }

  function handleReplyToMessage(repliedMessage) {
    setReplyingToMessage(repliedMessage);
  }

  function handleCancelReply() {
    setReplyingToMessage(null);
  }

  return {
    messages,
    hasMoreMessages: hasPreviousPage,
    message,
    isSending,
    textingPhoneNumber,
    isLoading: isFetching || isPropertyLoading || isPreferencesLoading,
    isPropertyLoading,
    isLoadingMore: isFetchingPreviousPage,
    messageMaxLength: textingCharacterLimitIncrease ? 1600 : 160,
    infoOptedIn: smsDoubleOptIn ? infoOptedIn : true,
    handleLoadMoreMessages,
    handleMessageChange,
    handleSendMessage,
    handleToggleResolved,
    replyingToMessage: leslieEnabled
      ? replyingToMessage ?? undefined
      : undefined,
    handleReplyToMessage: leslieEnabled ? handleReplyToMessage : undefined,
    handleCancelReply: leslieEnabled ? handleCancelReply : undefined,
  };
}
